import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowRight, ArrowLeft, Check, Loader2, AlertCircle, 
  ExternalLink, Play, Zap, User, Mail, Link2, CheckCircle, X
} from 'lucide-react';
import { API, POSITION_OPTIONS } from '../lib/constants';

// Position Selector Component
const PositionSelector = ({ selected = [], onChange }) => {
  const categories = [
    { key: 'offense', label: 'Offense', positions: POSITION_OPTIONS.offense },
    { key: 'defense', label: 'Defense', positions: POSITION_OPTIONS.defense },
    { key: 'specialTeams', label: 'Special Teams', positions: POSITION_OPTIONS.specialTeams },
  ];

  const toggle = (pos) => {
    onChange(selected.includes(pos) ? selected.filter(p => p !== pos) : [...selected, pos]);
  };

  return (
    <div className="space-y-3">
      {categories.map(cat => (
        <div key={cat.key}>
          <p className="text-xs text-white/40 mb-1.5 uppercase tracking-wider">{cat.label}</p>
          <div className="flex flex-wrap gap-1.5">
            {cat.positions.map(pos => (
              <button
                key={pos}
                type="button"
                onClick={() => toggle(pos)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                  selected.includes(pos)
                    ? 'bg-[#0F5132] text-white'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                {pos}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const CreateProfile = () => {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();

  // Step 1: Username & HUDL
  const [username, setUsername] = useState('');
  const [hudlUrl, setHudlUrl] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(null); // null, 'checking', 'available', 'taken'
  
  // Step 2: Profile data & videos
  const [profileData, setProfileData] = useState({
    name: '', school: '', class_year: '', positions: [],
    height: '', weight: '', jersey_number: '',
    profile_image_url: '', gpa: '', x_url: ''
  });
  const [videos, setVideos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [videoLoading, setVideoLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [allVideoUrls, setAllVideoUrls] = useState([]);

  // Prefill username from the homepage claim (?username=)
  useEffect(() => {
    const u = new URLSearchParams(window.location.search).get('username');
    if (u) setUsername(u);
  }, []);
  
  // Step 3: Auth
  const [email, setEmail] = useState('');
  const [creating, setCreating] = useState(false);

  // Username check with debounce
  useEffect(() => {
    if (!username || username.length < 3) {
      setUsernameStatus(null);
      return;
    }
    
    setUsernameStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/api/check-username`, { username });
        setUsernameStatus(res.data.available ? 'available' : 'taken');
      } catch {
        setUsernameStatus('taken');
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [username]);

  // Handle username input - only allow valid chars
  const handleUsernameChange = (e) => {
    const val = e.target.value.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 30);
    setUsername(val);
  };

  // X profile is required to continue (viral growth). Accept x.com / twitter.com links or a bare @handle.
  const isValidX = (v) => {
    const s = (v || '').trim();
    if (!s) return false;
    return /(^@?[A-Za-z0-9_]{1,15}$)|(^https?:\/\/(www\.)?(x|twitter)\.com\/.+)/i.test(s);
  };
  const xReady = isValidX(profileData.x_url);

  // Step 1 → 2: Fetch HUDL data (always advances; import is best-effort)
  const fetchHudlData = async () => {
    if (!hudlUrl || usernameStatus !== 'available' || !xReady) return;

    setProfileLoading(true);
    try {
      const profileRes = await axios
        .post(`${API}/api/preview-hudl-basic`, { hudl_profile_url: hudlUrl }, { timeout: 60000 })
        .catch(() => null);

      if (profileRes?.data?.success) {
        const data = profileRes.data.profile_data || {};
        setProfileData(prev => ({
          ...prev,
          name: data.name || prev.name,
          school: data.school || prev.school,
          class_year: data.class_year || prev.class_year,
          positions: data.positions || prev.positions,
          height: data.height || prev.height,
          weight: data.weight || prev.weight,
          jersey_number: data.jersey_number || prev.jersey_number,
          profile_image_url: data.profile_image_url || prev.profile_image_url,
          location: data.location || prev.location,
        }));
        toast.success('HUDL profile imported!');
      } else {
        toast.warning("Couldn't fully import from HUDL — you can add details on the next step.");
      }
    } catch (err) {
      console.error('HUDL import error:', err);
      toast.warning("Couldn't reach HUDL — you can add details on the next step.");
    } finally {
      setProfileLoading(false);
    }

    // Always continue to the review step so onboarding is never blocked
    setStep(2);

    // Fetch highlight video links in the background (non-blocking)
    setVideoLoading(true);
    axios
      .post(`${API}/api/list-hudl-videos`, { hudl_profile_url: hudlUrl }, { timeout: 60000 })
      .then(videoRes => {
        if (videoRes.data?.success && videoRes.data.video_urls) {
          const videoUrls = videoRes.data.video_urls;
          setAllVideoUrls(videoUrls);
          if (videoUrls.length > 0) {
            toast.success(`${videoUrls.length} highlight video${videoUrls.length > 1 ? 's' : ''} found!`);
          }
        }
      })
      .catch(err => console.log('Video fetch failed:', err))
      .finally(() => setVideoLoading(false));
  };

  // Step 1 without HUDL
  const skipHudl = () => {
    setStep(2);
  };

  // Toggle video selection
  const toggleVideo = (idx) => {
    setSelectedVideos(prev => 
      prev.includes(idx) ? prev.filter(i => i !== idx) : prev.length < 3 ? [...prev, idx] : prev
    );
  };

  // Create passwordless profile — backend logs the athlete in immediately
  const createProfile = async () => {
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    setCreating(true);

    try {
      // First 3 HUDL videos are auto-added to the profile; the rest go to the dashboard shelf (handled server-side)
      const selectedVideoData = [];
      const remainingVideoUrls = allVideoUrls;

      const res = await axios.post(`${API}/api/confirm-hudl-import`, {
        username,
        email,
        profile_data: {
          ...profileData,
          hudl_url: hudlUrl,
        },
        selected_video_data: selectedVideoData,
        remaining_video_urls: remainingVideoUrls,
      }, { withCredentials: true });

      if (res.data.success) {
        localStorage.setItem('userId', res.data.user_id);
        localStorage.setItem('username', res.data.username);
        localStorage.setItem('userEmail', email);
        toast.success('Profile created! Verify your account to go live.');
        navigate('/dashboard');
      } else if (res.data.claim_existing) {
        toast.success(res.data.message || 'We already built this page — claim it!');
        navigate(`/claim/${res.data.username}`);
      } else if (res.data.already_claimed) {
        toast.error(res.data.message || 'This profile already exists and is claimed. Try signing in.');
        navigate('/signin');
      }
    } catch (err) {
      console.error('Profile creation failed:', err);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (err.message || 'Something went wrong');
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  // Stepper progress
  const steps = [
    { num: 1, label: 'Info' },
    { num: 2, label: 'Profile' },
    { num: 3, label: 'Go Live' },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 bg-[#1a1a1a] rounded-full flex items-center justify-center">
              <span className="text-[#16A34A] font-black text-xs">O</span>
            </div>
            <span className="text-lg font-black">Offersz</span>
          </div>
          
          {/* Stepper */}
          <div className="flex items-center gap-2">
            {steps.map((s, i) => (
              <React.Fragment key={s.num}>
                {i > 0 && <div className={`w-6 h-0.5 ${step >= s.num ? 'bg-[#16A34A]' : 'bg-white/10'}`} />}
                <div className={`flex items-center gap-1.5 ${step >= s.num ? 'text-[#16A34A]' : 'text-white/30'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    step > s.num ? 'bg-[#0F5132] text-white' : step === s.num ? 'bg-[#16A34A]/20 border border-[#16A34A]' : 'bg-white/10'
                  }`}>
                    {step > s.num ? <Check className="h-3 w-3" /> : s.num}
                  </div>
                  <span className="text-xs hidden sm:block">{s.label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="pt-14 min-h-screen flex items-start justify-center px-4 pb-8">
        <div className="w-full max-w-lg mt-8 sm:mt-16">
          
          {/* Step 1: Username & HUDL */}
          {step === 1 && (
            <div className="space-y-8 animate-in fade-in">
              <div>
                <h1 className="text-3xl sm:text-4xl font-black mb-2">Let's build your profile</h1>
                <p className="text-white/50">Pick a username and drop your HUDL link. We'll do the rest.</p>
              </div>

              {/* Username */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-white/70 flex items-center gap-2">
                  <User className="h-4 w-4" /> Username
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm">offersz.co/</span>
                  <Input
                    value={username}
                    onChange={handleUsernameChange}
                    placeholder="yourname"
                    data-testid="onboarding-username"
                    className="bg-white/5 border-white/10 text-white pl-24 h-12 text-lg rounded-xl focus:border-[#16A34A] focus:ring-[#16A34A]/20"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {usernameStatus === 'checking' && <Loader2 className="h-4 w-4 animate-spin text-white/30" />}
                    {usernameStatus === 'available' && <CheckCircle className="h-5 w-5 text-green-400" />}
                    {usernameStatus === 'taken' && <X className="h-5 w-5 text-red-400" />}
                  </div>
                </div>
                {usernameStatus === 'taken' && (
                  <p className="text-red-400 text-xs flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Username taken</p>
                )}
                {usernameStatus === 'available' && (
                  <p className="text-green-400 text-xs flex items-center gap-1"><Check className="h-3 w-3" /> Available!</p>
                )}
              </div>

              {/* HUDL URL */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-white/70 flex items-center gap-2">
                  <Link2 className="h-4 w-4" /> HUDL Profile URL
                </label>
                <Input
                  value={hudlUrl}
                  onChange={(e) => setHudlUrl(e.target.value)}
                  placeholder="https://www.hudl.com/profile/..."
                  className="bg-white/5 border-white/10 text-white h-12 text-lg rounded-xl focus:border-[#16A34A] focus:ring-[#16A34A]/20"
                />
                <p className="text-white/30 text-xs">
                  We'll auto-import your profile picture, stats, and highlight videos.
                </p>
              </div>

              {/* X (Twitter) URL — required for viral growth */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-white/70 flex items-center gap-2">
                  <X className="h-4 w-4" /> X (Twitter) Profile
                  <span className="text-[#16A34A] text-xs font-bold ml-1">Required</span>
                </label>
                <Input
                  value={profileData.x_url}
                  onChange={(e) => setProfileData(p => ({ ...p, x_url: e.target.value }))}
                  placeholder="https://x.com/yourhandle"
                  data-testid="onboarding-x-url"
                  className="bg-white/5 border-white/10 text-white h-12 text-lg rounded-xl focus:border-[#16A34A] focus:ring-[#16A34A]/20"
                />
                <p className="text-white/30 text-xs">
                  Players land on your Offersz link straight from your X bio — this is how you go viral.
                </p>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <Button
                  onClick={fetchHudlData}
                  disabled={!username || username.length < 3 || usernameStatus !== 'available' || !hudlUrl || !xReady || profileLoading}
                  className="w-full bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold h-12 rounded-xl text-lg disabled:opacity-50"
                >
                  {profileLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Importing from HUDL...
                    </>
                  ) : (
                    <>
                      Import & Continue
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
                
                <button
                  onClick={skipHudl}
                  disabled={!username || username.length < 3 || usernameStatus !== 'available' || !xReady}
                  className="w-full text-white/30 hover:text-white/50 text-sm py-2 transition-colors disabled:opacity-30"
                >
                  Skip HUDL — I'll add info manually
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Review/Edit Profile */}
          {step === 2 && (
            <div className="space-y-6 animate-in fade-in">
              <div>
                <h1 className="text-3xl sm:text-4xl font-black mb-2">Review your profile</h1>
                <p className="text-white/50">Edit anything that doesn't look right.</p>
              </div>

              {/* Profile Preview Card */}
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden">
                {/* Header with photo */}
                <div className="bg-gradient-to-r from-[#16A34A]/10 to-[#16A34A]/5 p-6 flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
                    {profileData.profile_image_url ? (
                      <img src={profileData.profile_image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-white/40">
                        {profileData.name?.charAt(0) || username?.charAt(0) || '?'}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{profileData.name || 'Your Name'}</h3>
                    <p className="text-white/50 text-sm">{profileData.school || 'School'} • Class of {profileData.class_year || '20XX'}</p>
                    <p className="text-[#16A34A] text-xs mt-1">offersz.co/{username}</p>
                  </div>
                </div>

                {/* Editable Fields */}
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">Full Name</label>
                      <Input 
                        value={profileData.name} 
                        onChange={e => setProfileData(p => ({...p, name: e.target.value}))}
                        className="bg-white/5 border-white/10 text-white h-10 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">School</label>
                      <Input 
                        value={profileData.school}
                        onChange={e => setProfileData(p => ({...p, school: e.target.value}))}
                        className="bg-white/5 border-white/10 text-white h-10 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">Class Year</label>
                      <Input 
                        value={profileData.class_year}
                        onChange={e => setProfileData(p => ({...p, class_year: e.target.value}))}
                        className="bg-white/5 border-white/10 text-white h-10 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">Jersey #</label>
                      <Input 
                        value={profileData.jersey_number}
                        onChange={e => setProfileData(p => ({...p, jersey_number: e.target.value}))}
                        className="bg-white/5 border-white/10 text-white h-10 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">Height</label>
                      <Input 
                        value={profileData.height}
                        onChange={e => setProfileData(p => ({...p, height: e.target.value}))}
                        placeholder="6'2&quot;"
                        className="bg-white/5 border-white/10 text-white h-10 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">Weight (lbs)</label>
                      <Input 
                        value={profileData.weight}
                        onChange={e => setProfileData(p => ({...p, weight: e.target.value}))}
                        className="bg-white/5 border-white/10 text-white h-10 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/40 mb-1 block">GPA</label>
                      <Input 
                        value={profileData.gpa}
                        onChange={e => setProfileData(p => ({...p, gpa: e.target.value}))}
                        placeholder="3.5"
                        className="bg-white/5 border-white/10 text-white h-10 rounded-lg"
                      />
                    </div>
                  </div>

                  {/* Positions */}
                  <div>
                    <label className="text-xs text-white/40 mb-2 block">Positions</label>
                    <PositionSelector
                      selected={profileData.positions}
                      onChange={positions => setProfileData(p => ({...p, positions}))}
                    />
                  </div>
                </div>
              </div>

              {/* Highlights found — added from dashboard after signup */}
              {(allVideoUrls.length > 0 || videoLoading) && (
                <div data-testid="onboarding-videos-info" className="bg-[#16A34A]/[0.06] border border-[#16A34A]/20 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#16A34A]/15 flex items-center justify-center flex-shrink-0">
                      {videoLoading ? <Loader2 className="h-5 w-5 animate-spin text-[#16A34A]" /> : <Play className="h-5 w-5 text-[#16A34A]" />}
                    </div>
                    <div className="min-w-0">
                      {videoLoading ? (
                        <p className="text-sm font-medium">Finding your HUDL highlights…</p>
                      ) : (
                        <>
                          <p className="text-sm font-bold">{allVideoUrls.length} highlight{allVideoUrls.length !== 1 ? 's' : ''} found</p>
                          <p className="text-xs text-white/50">Your top {Math.min(3, allVideoUrls.length)} will be added to your profile automatically — the rest wait in your dashboard to add anytime.</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  className="border-white/10 text-white hover:bg-white/5 rounded-xl"
                >
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!profileData.name}
                  className="flex-1 bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold h-12 rounded-xl text-lg"
                >
                  Continue
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Auth & Create */}
          {step === 3 && (
            <div className="space-y-8 animate-in fade-in">
              <div>
                <h1 className="text-3xl sm:text-4xl font-black mb-2">Go live!</h1>
                <p className="text-white/50">Just your email — no password. You'll verify your account from your dashboard.</p>
              </div>

              {/* Profile Summary */}
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
                  {profileData.profile_image_url ? (
                    <img src={profileData.profile_image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-bold text-white/40">
                      {profileData.name?.charAt(0) || '?'}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{profileData.name}</p>
                  <p className="text-white/40 text-xs">{profileData.school} • {profileData.positions?.join(', ') || 'Football Player'}</p>
                </div>
                <Badge className="bg-[#16A34A]/10 text-[#16A34A] border-[#16A34A]/20">
                  {allVideoUrls.length} video{allVideoUrls.length !== 1 ? 's' : ''} ready
                </Badge>
              </div>

              {/* Email */}
              <div className="space-y-3">
                <label className="text-sm font-semibold text-white/70 flex items-center gap-2">
                  <Mail className="h-4 w-4" /> Email Address
                </label>
                <Input
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  type="email"
                  placeholder="you@email.com"
                  data-testid="create-email-input"
                  onKeyPress={e => e.key === 'Enter' && createProfile()}
                  className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
                />
                <p className="text-white/30 text-xs">We'll send a 6-digit code here to verify your account — no password to remember.</p>
              </div>

              {/* Navigation */}
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(2)}
                  className="border-white/10 text-white hover:bg-white/5 rounded-xl"
                >
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={createProfile}
                  disabled={!email || creating}
                  data-testid="create-profile-submit-btn"
                  className="flex-1 bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold h-12 rounded-xl text-lg disabled:opacity-50"
                >
                  {creating ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Creating Profile...
                    </>
                  ) : (
                    <>
                      <Zap className="mr-2 h-5 w-5" />
                      Create My Profile
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateProfile;
